// import type { Note } from "../../../annotator-queue/types/responseTypes";
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@verantos-dev/core-annotation-tool';
import type { DropdownListItem } from '@verantos-dev/shared-components';

import { useGetAnnotationWorkspaceQuery } from '../../api/annotationApiSlice';
import { clearFilterBy } from '../../state/annotationSlice';
import { selectFilterBy, selectNoteSortBy } from '../../state/selector';
import type { Note } from '../../types/response';

export interface UseNoteListSidebarResult {
  notes: Note[];
  filterOptions: DropdownListItem[];
}

export const useNoteListSidebar = (): UseNoteListSidebarResult => {
  const dispatch = useAppDispatch();
  const filterBy = useAppSelector(selectFilterBy);
  const noteSortBy = useAppSelector(selectNoteSortBy);
  const { taskId } = useParams();

  const { data: annotationWorkspaceData } = useGetAnnotationWorkspaceQuery({
    taskId: taskId as string,
  });

  const notes = annotationWorkspaceData?.data?.notes ?? [];

  const filterOptions: DropdownListItem[] = useMemo(() => {
    const uniqueNoteTypes = [...new Set(notes.map((note) => note.noteType))];

    const noteTypeOptions = uniqueNoteTypes.map((noteType) => ({
      value: noteType,
      displayValue: noteType,
    }));

    // Add 'All' option at the beginning and keep it selected by default
    return [{ value: 'all', displayValue: 'All' }, ...noteTypeOptions];
  }, [notes]);

  useEffect(() => {
    dispatch(clearFilterBy());
  }, [filterOptions, dispatch]);

  const filteredNotes = useMemo(() => {
    if (filterBy === 'all') {
      return notes;
    }
    return notes.filter((note) => note.noteType === filterBy);
  }, [notes, filterBy]);

  const sortedNotes = useMemo(() => {
    const notesCopy = [...filteredNotes];

    if (noteSortBy === 'ascending') {
      return notesCopy.sort((a, b) => a.noteDatetime.localeCompare(b.noteDatetime));
    }

    return notesCopy.sort((a, b) => b.noteDatetime.localeCompare(a.noteDatetime));
  }, [filteredNotes, noteSortBy]);

  return {
    notes: sortedNotes,
    filterOptions,
  };
};
